/**
 * #4466: when claiming a coach invitation fails, what do we tell the person?
 *
 * A coach invitation link carries a one-use token. The client opens the link, and the app calls
 * the server to "claim" it — which connects the two accounts and copies over the program the coach
 * prepared. That call can fail in several ways, and they do NOT all mean the same thing.
 *
 * The bug this file exists to prevent is a single sentence being used for all of them:
 *
 *     "This invitation link is invalid or has already been used."
 *
 * That sentence is a claim about the TOKEN. It is only true when the server actually looked at the
 * token and rejected it. Every other failure — sign-in had not finished yet, the network dropped,
 * the server returned a 500 — never got a verdict on the token at all. The link is still perfectly
 * good in those cases, and saying otherwise sends a brand-new client away from a working invite on
 * the very first screen they ever see. Both of those causes are real: they are the two error shapes
 * recorded on Sentry issue 7469456257.
 *
 * There is a THIRD state, and it is the one that is easiest to get backwards. The claim call can
 * succeed and something AFTER it fail — writing the copied program to the local database, or
 * pushing it to the server. By then the server has already marked the slot claimed, so the token is
 * spent: telling that person "your link is still valid, open it again" walks them straight into the
 * "invalid or already used" refusal, which is the exact dead end this whole change is about. They
 * are in fact connected to their coach; it is the program copy that did not finish.
 *
 * So this module keeps two questions apart, and the second one is the whole point:
 *
 *   - WHAT KIND of failure was it?   -> classifyCoachClaimFailure
 *   - What may we truthfully say?    -> pickCoachClaimFailureMessage
 *
 * The same split applies one call EARLIER, when the app merely checks whether a link is still good
 * before offering to claim it -> decideCoachTokenCheckOutcome / classifyCoachTokenCheckFailure. That
 * call had the identical bug and is the one this module gained second; its own comment says why.
 *
 * Pure on purpose (no Angular imports), so it can be unit-tested under plain Node. Same shape as
 * pricing-failure-policy.ts, which exists for the same reason on the subscribe page.
 */

/**
 * What CHECKING an invitation token concluded, before anybody tries to claim it.
 *
 * This is the same mistake as the one above, one call earlier, and it was recorded as a deferred
 * finding at the time #4466 was fixed. `validateTokenAsync` asks the server whether a link is still
 * good; its answer used to be a plain boolean, so a dropped connection, a timeout and a 500 all came
 * back as `false` — indistinguishable from the server having looked and said no. The login page
 * believed that `false` and showed "this invitation link is invalid or has already been used".
 *
 * Three states rather than two, because "I could not look" is not an answer about the token.
 */
export enum CoachTokenCheckOutcome {
  /** The server looked and said the token is good. */
  Valid = 'valid',
  /** The server looked and said no. This is the only outcome that may call the link bad. */
  RejectedByServer = 'rejected-by-server',
  /** Nobody ever reached a verdict. The link is probably still fine; trying again is worth doing. */
  CouldNotCheck = 'could-not-check',
}

/**
 * Why a token check reached no verdict. Only ever accompanies `CouldNotCheck`.
 *
 * This exists to be put in the Sentry report NAME. Sentry context becomes a breadcrumb and is not
 * indexed, so naming the cause only in the context would group every cause back into one issue
 * nobody can split — which is the same trap #4466 had to fix on the claim call.
 */
export enum CoachTokenCheckFailureKind {
  /** The request never got an answer: the device is offline, the connection dropped, it timed out. */
  CouldNotReachServer = 'could-not-reach-server',
  /** The request reached the server and the server failed (HTTP 5xx). */
  ServerFailed = 'server-failed',
  /** The server answered normally and said it could not complete the check — its own read failed. */
  ServerCouldNotCheck = 'server-could-not-check',
  /** A failure we cannot explain. Still not evidence that the token is bad. */
  Unexpected = 'unexpected',
}

/**
 * Decide what the check concluded.
 *
 * Note the order: not having heard back beats everything the answer might have said, because when
 * the call threw there IS no answer to read. The `couldNotCheck` flag is checked before `valid`
 * for the same reason — the server is telling us its own read failed, so its `valid: false` is not
 * a verdict either.
 */
export function decideCoachTokenCheckOutcome(input: {
  /** Did the server answer at all? False when the call threw. */
  serverAnswered: boolean;
  /** The server answered and said the token is good. */
  serverSaysValid: boolean;
  /** The server answered, but said it could not complete the check itself. */
  serverCouldNotCheck: boolean;
}): CoachTokenCheckOutcome {
  if (!input.serverAnswered) return CoachTokenCheckOutcome.CouldNotCheck;
  if (input.serverCouldNotCheck) return CoachTokenCheckOutcome.CouldNotCheck;
  return input.serverSaysValid ? CoachTokenCheckOutcome.Valid : CoachTokenCheckOutcome.RejectedByServer;
}

/**
 * Name the cause, for the report only. Never decides what the person is told.
 *
 * Takes plain booleans rather than the error object, so this module stays free of the
 * cloud-function error classes and their import chain — the same reason
 * `classifyCoachClaimFailure` above does.
 */
export function classifyCoachTokenCheckFailure(input: {
  /** Did the server answer? If it did, the only way to be here is its own `couldNotCheck`. */
  serverAnswered: boolean;
  /** Could we not reach the server at all (network, timeout)? */
  isServerUnreachable: boolean;
  /** Did the server answer with an HTTP 5xx? */
  isServerError: boolean;
}): CoachTokenCheckFailureKind {
  if (input.serverAnswered) return CoachTokenCheckFailureKind.ServerCouldNotCheck;
  if (input.isServerUnreachable) return CoachTokenCheckFailureKind.CouldNotReachServer;
  if (input.isServerError) return CoachTokenCheckFailureKind.ServerFailed;
  return CoachTokenCheckFailureKind.Unexpected;
}

export enum CoachClaimFailureKind {
  /** The server looked at the token and said no: it really is invalid, or already used. */
  TokenRejected = 'token-rejected',
  /**
   * The server ACCEPTED the claim, and something after that failed. The token is spent and the
   * accounts are linked, but the program copy did not finish.
   */
  SetupFailedAfterClaimAccepted = 'setup-failed-after-claim-accepted',
  /**
   * The call never left with an auth token, because sign-in had not finished settling. Seen on a
   * warm resume, where the app restores itself from cache before Firebase's auth listener fires.
   */
  NotSignedInYet = 'not-signed-in-yet',
  /** We could not reach the server, or the server itself failed: a dropped network, a timeout, a 5xx. */
  CouldNotReachServer = 'could-not-reach-server',
  /**
   * The app's own database had not finished starting up, so we refused to claim at all
   * (#4801). We never called the server, which is the whole point: the token is still
   * unspent and the same link will work on the next healthy start-up.
   *
   * Claiming anyway is what this is here to prevent. On 2026-08-07 a client's start-up
   * had actually FAILED — the app was showing its fatal-error screen in the same second
   * the claim ran — and the claim wrote a coach's program into documents that were never
   * loaded, which copied nothing and reported nothing.
   */
  DatabaseNotReady = 'database-not-ready',
  /** A failure we cannot explain. Reported, but still not evidence that the token is bad. */
  Unexpected = 'unexpected',
}

/** What we may truthfully put in front of the person. One per wording, not one per cause. */
export enum CoachClaimFailureMessage {
  /** "This invitation link is invalid or has already been used." */
  LinkIsBad = 'link-is-bad',
  /** "We could not finish setting up your program. Your invitation link is still valid — open it again." */
  ReopenTheLink = 'reopen-the-link',
  /** "You are connected to your coach, but we could not finish copying your program." */
  ConnectedButSetupUnfinished = 'connected-but-setup-unfinished',
}

/**
 * Decide which kind of failure this was.
 *
 * Takes plain booleans rather than the error object so the module stays free of the cloud-function
 * error classes and their import chain — the caller already has those and can answer each question
 * in one line.
 *
 * The order below is a precedence, not a style choice. Anything the SERVER did is stronger evidence
 * than anything we can infer from the error's shape, so the two server answers are checked first.
 */
export function classifyCoachClaimFailure(input: {
  /** Did the server accept the claim, meaning the failure happened after the token was spent? */
  serverAcceptedClaim: boolean;
  /** Did the server answer, and its answer was "no, that token is not valid"? */
  serverRejectedToken: boolean;
  /** Was the error our own "there is no auth token yet" error (CloudFunctionAuthError)? */
  isAuthNotReady: boolean;
  /** Could we not reach the server, or did the server itself fail (network, timeout, 5xx)? */
  isServerUnreachableOrFailed: boolean;
}): CoachClaimFailureKind {
  if (input.serverAcceptedClaim) return CoachClaimFailureKind.SetupFailedAfterClaimAccepted;
  if (input.serverRejectedToken) return CoachClaimFailureKind.TokenRejected;
  if (input.isAuthNotReady) return CoachClaimFailureKind.NotSignedInYet;
  if (input.isServerUnreachableOrFailed) return CoachClaimFailureKind.CouldNotReachServer;
  return CoachClaimFailureKind.Unexpected;
}

/**
 * Pick what to say about this failure.
 *
 * Only a server rejection may call the link bad. Everything that failed BEFORE any verdict existed
 * gets told the truth — the link still works, open it again — because claiming otherwise makes the
 * person give up on a good invite. "We do not know what went wrong" is not evidence about the token
 * either, which is why the unexplained case answers the same way.
 */
export function pickCoachClaimFailureMessage(kind: CoachClaimFailureKind): CoachClaimFailureMessage {
  // every kind is listed rather than falling through a `default`. This module exists to stop the
  // code asserting something it does not know, and a default would quietly hand the next kind
  // somebody adds a claim about the token — most likely the wrong one, since the kind this file
  // had to gain most recently was exactly a post-verdict one. Listing them makes TypeScript ask.
  switch (kind) {
    case CoachClaimFailureKind.TokenRejected:
      return CoachClaimFailureMessage.LinkIsBad;
    case CoachClaimFailureKind.SetupFailedAfterClaimAccepted:
      return CoachClaimFailureMessage.ConnectedButSetupUnfinished;
    // Everything below failed BEFORE the server ever gave a verdict on the token, so the
    // link is still good. DatabaseNotReady is the strongest case of the four: we refused
    // before making the call at all, so the token is provably unspent (#4801).
    case CoachClaimFailureKind.NotSignedInYet:
    case CoachClaimFailureKind.CouldNotReachServer:
    case CoachClaimFailureKind.DatabaseNotReady:
    case CoachClaimFailureKind.Unexpected:
      return CoachClaimFailureMessage.ReopenTheLink;
  }
}
